import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import type {
  DispatchableAction,
  RootAction,
  ThunkResult,
} from '../../types/redux';
import * as Actions from './actions';
import * as Selectors from './selectors';
import * as Thunks from './thunks';
import * as Types from './types';

// Hook that simplifies dispatching redux view part actions, by binding them
// to a specific view part reference.
export function useViewAction<
  RefType extends Types.AllPartRefs,
  ArgType extends any[],
>(ref: RefType, fn: (ref: RefType, ...fnArgs: ArgType) => DispatchableAction) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => dispatch(fn(ref, ...args)),
    [dispatch, ref, fn]
  );
}

export function useMaybeViewAction<
  RefType extends Types.AllPartRefs,
  ArgType extends any[],
>(
  ref: RefType | null | undefined,
  fn: (ref: RefType, ...fnArgs: ArgType) => DispatchableAction
) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => (ref != null ? dispatch(fn(ref, ...args)) : void 0),
    [dispatch, ref, fn]
  );
}

// Like the above version, but binds all arguments. Useful when an action requires multiple refs.
export function useViewActionBindAll<
  RefType extends Types.AllPartRefs,
  ArgType extends any[],
>(
  fn: (ref: RefType, ...fnArgs: ArgType) => RootAction,
  ref: RefType,
  ...args: ArgType
) {
  const dispatch = useDispatch();
  return useCallback(() => {
    return dispatch(fn(ref, ...args));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ref, fn, ...args]);
}

// Hook that simplifies dispatching redux view actions on a nullable view ref.
export function useViewRefAction<
  RefType extends Types.ViewRef,
  ArgType extends any[],
>(ref: RefType | null, fn: (ref: RefType, ...fnArgs: ArgType) => RootAction) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => (ref != null ? dispatch(fn(ref, ...args)) : void 0),
    [dispatch, ref, fn]
  );
}

export function useViewRefThunk<
  RefType extends Types.ViewRef,
  ArgType extends any[],
  ThunkType extends ThunkResult<any>,
>(ref: RefType | null, fn: (ref: RefType, ...fnArgs: ArgType) => ThunkType) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => (ref != null ? dispatch(fn(ref, ...args)) : void 0),
    [dispatch, ref, fn]
  );
}

export function useObjectCopy<RefType extends Types.AllPartRefs>(ref: RefType) {
  const [copyRef, setCopyRef] = useState<RefType | null>(null);

  const dispatch = useDispatch();
  useEffect(() => {
    // Copy the part on mount
    const action = Actions.copyObject(ref);
    // copy object is used for the panel editor, and unfortunately copying a panel
    // can be expensive for two reasons:
    // 1. panels parts is stored is as single object, and we do an immutable update
    //    on it. Immutable updates start getting expensive when you have 100s of
    //    thousands of keys on an object.
    // 2. we have some expensive selectors (panelBankConfig/selectors.ts) that fire
    //    any time panels parts changes.
    // So we dispatch this after a set timeout, so that the PanelEditor can display
    // display immediately with a loader, while copy/dispatch/expensive rendering
    // happens.
    // We could optimize all those things, but for now we just optimize for showing
    // users some feedback as soon as possible.
    setTimeout(() => dispatch(action), 1);
    const newRef = action.payload.ref as RefType;
    setCopyRef(newRef);
    return () => {
      // Clean up on unmount
      setCopyRef(null);
      dispatch(Thunks.deleteObject(newRef));
    };
  }, [dispatch, ref]);

  // Becomes true once the copy action succeeds
  const partExists = useSelector(Selectors.makePartExistsSelector(copyRef));

  if (copyRef != null && partExists) {
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-as-const
      copying: false as false,
      ref: copyRef,
    };
  }
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  return {copying: true as true};
}

// This could be more explicitly called useTemporaryWhole but that's
// verbose and ugly. So trying out useNewObjectRef to see how it feels.
export function useNewObjectRef<T extends Types.ObjType>(
  type: T,
  whole: Types.WholeFromType<T>
) {
  return useExistingOrNewObjectRef(type, whole);
}

export function useExistingOrNewObjectRef<T extends Types.ObjType>(
  type: T,
  whole: Types.WholeFromType<T>,
  existing?: Types.PartRefFromType<T>
) {
  const [ref, setRef] = useState<Types.PartRefFromType<T> | null>(null);

  const dispatch = useDispatch();
  useEffect(() => {
    if (existing != null) {
      return;
    }
    const wholeAndType = {
      type,
      whole,
    } as unknown as Types.AllWholeWithTypesHelper<T>;
    // Add the object on mount
    const action = Actions.addObject(wholeAndType);
    dispatch(action);
    const newRef = action.payload.ref as Types.PartRefFromType<T>;
    setRef(newRef);

    // Clean up on unmount
    return () => {
      dispatch(Thunks.deleteObject(newRef));
      setRef(null);
    };
  }, [dispatch, type, whole, existing]);

  // Becomes true once the copy action succeeds
  const partExists = useSelector(Selectors.makePartExistsSelector(ref));

  if (existing != null) {
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-as-const
      ready: true as true,
      ref: existing,
    };
  }
  if (ref != null && partExists) {
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-as-const
      ready: true as true,
      ref,
    };
  }
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  return {ready: false as false};
}

/**
 * Subscribe to an entire normalized part object. Re-renders whenever ANY property of the part changes.
 *
 * When to use:
 * - Need most/all properties from the part
 * - Denormalizing (reconstructing whole objects from normalized parts)
 *
 * When to avoid:
 * - Only need 1-2 properties (use reselect selectors with usePropsSelector)
 * - Component is deeply nested and part changes frequently (causes cascading re-renders)
 *
 * ⚠️ WARNING: Context providers
 *
 * Do NOT use usePart in components that provide a ref via context (e.g., RunSelectorRunSetProvider).
 * Deeply nested consumers will re-render on ANY property change, causing expensive cascading
 * re-renders throughout the tree. Instead, provide only the ref via context and use
 * reselect selectors from components/RunSelector/runSetSelectors.ts with usePropsSelector.
 *
 * @example
 * // Good: Need nested refs to chain into other hooks
 * const runSet = usePart(runSetRef);
 * const filters = usePart(runSet.filtersRef);  // Need the ref from runSet
 *
 * @see usePropsSelector with reselect selectors for extracting specific properties efficiently
 * @see usePartMapped for prop-dependent or complex transformations
 */
export function usePart<T extends Types.ObjType>(
  ref: Types.PartRefFromType<T>
) {
  const selector = useMemo(() => Selectors.makePartSelector(ref), [ref]);
  return useSelector(selector);
}

export function usePartMaybe<T extends Types.ObjType>(
  ref?: Types.PartRefFromType<T> | null
) {
  const selector = useMemo(
    () => (ref != null ? Selectors.makePartSelector(ref) : () => undefined),
    [ref]
  );
  return useSelector(selector);
}

export function useParts<T extends Types.ObjType>(
  refs: Array<Types.PartRefFromType<T>>
) {
  const selector = useMemo(() => Selectors.makePartsSelector(refs), [refs]);
  return useSelector(selector);
}

export function useOptionalPart<T extends Types.ObjType>(
  ref: Types.PartRefFromType<T> | undefined
) {
  const selector = useMemo(
    () => Selectors.makeOptionalPartSelector(ref),
    [ref]
  );
  return useSelector(selector);
}

/**
 * Select and transform data from a normalized part using a mapper function.
 * Uses lodash isEqual (deep comparison) to determine if the mapped result changed.
 *
 * Use when:
 * - Mapper **depends on props or local state** (must be inline/unstable)
 * - Mapper returns **deeply nested objects** that need deep comparison
 * - Mapper **creates new arrays/objects** each call (isEqual handles this)
 *
 * Avoid when:
 * - Mapper only extracts refs/primitives → use reselect selectors with usePropsSelector
 * - Performance is critical → use reselect selectors with usePropsSelector
 * - Mapper can be stable (defined outside component) → use reselect selectors
 *
 * ## Why this tolerates inline mappers
 *
 * This hook uses an internal stateRef to cache results:
 * ```
 * const stateRef = useRef({});  // Persists across renders
 * return useSelector(makePartMappedSelector(ref, map, stateRef.current));
 * ```
 * Even if the mapper is recreated each render, isEqual prevents re-renders
 * when the result is structurally the same.
 *
 * ⚠️ Performance cost
 *
 * On every Redux state change:
 * 1. Mapper function runs (even if irrelevant state changed)
 * 2. isEqual deeply compares new result vs cached result
 *
 * @example
 * // Good: Mapper depends on props.row - must be inline
 * const checkboxState = usePartMapped(
 *   groupSelectionsRef,
 *   selections => getCheckedState(selections, props.row)
 * );
 *
 * @see usePropsSelector with reselect selectors for better performance when mapper can be stable
 */
export function usePartMapped<T extends Types.ObjType, R>(
  ref: Types.PartRefFromType<T>,
  map: (part: Types.PartFromType<T>) => R
) {
  const stateRef = useRef({});
  return useSelector(
    Selectors.makePartMappedSelector(
      ref,
      map as (t: any) => R,
      stateRef.current
    )
  );
}
